// "BigQuery insights" panel appended to the bottom of /dashboard. The MySQL
// cards above it are unchanged; this adds what only BigQuery has: the AI app
// (Postgres) next to the legacy app, pours by machine, the website, Braze, and
// pipeline freshness.

import React, { useState } from "react";
import Chart from "react-apexcharts";
import { COLORS, Change, DataTable, DaysPicker, Section, StateBanner, Tile, chartTheme, fmtInt, fmtMoney, fmtPct, useBqInsights } from "./bqShared.jsx";

const EVENT_LABELS = {
  drink_mixed: "Drinks crafted",
  device_connected: "Machine connections",
  session_started: "App sessions",
  recipe_viewed: "Recipe views",
  search: "Searches",
  recipe_favourited: "Recipes favourited",
  recipe_liked: "Recipes liked",
  mixlist_viewed: "Mixlist views",
  ingredient_viewed: "Ingredient views",
  content_viewed: "Content views",
};

export default function BqDashboardInsights() {
  const [days, setDays] = useState(7);
  const { data, loading, error } = useBqInsights("dashboard", days);
  const t = chartTheme();

  const machines = data?.pours_by_machine_30d || [];
  const machineChart = {
    series: [
      { name: "Legacy app", data: machines.map((m) => m.legacy_app) },
      { name: "Barsys AI app", data: machines.map((m) => m.ai_app) },
    ],
    options: {
      chart: { ...t.chart, type: "bar", stacked: true },
      colors: [COLORS[5], COLORS[0]],
      plotOptions: { bar: { horizontal: true, barHeight: "60%" } },
      xaxis: { categories: machines.map((m) => m.machine), labels: t.xaxisLabels },
      yaxis: { labels: t.yaxisLabels },
      legend: t.legend, grid: t.grid, tooltip: t.tooltip, dataLabels: t.dataLabels,
    },
  };

  const w = data?.website_30d;
  const b = data?.braze;

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center flex-wrap mb-3">
        <h4 className="mb-0">
          BigQuery insights <small className="text-inverse text-opacity-50">legacy app + Barsys AI app, website, messaging</small>
        </h4>
        <div className="ms-auto">
          <DaysPicker days={days} setDays={setDays} disabled={loading} options={[7, 14, 30]} />
        </div>
      </div>
      <StateBanner loading={loading} error={error} hasData={!!data} />

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="row">
            <div className="col-xl-7">
              <Section title="APP ACTIVITY" subtitle={`last ${data.window_days} days vs previous ${data.window_days}`} bodyClass="p-0">
                <DataTable
                  rows={data.activity}
                  columns={[
                    { key: "event", label: "Metric", render: (r) => EVENT_LABELS[r.event] || r.event },
                    { key: "total", label: "Total", num: true, render: (r) => fmtInt(r.total.current) },
                    { key: "chg", label: "vs prev", num: true, render: (r) => <Change value={r.total.change_pct} /> },
                    { key: "legacy", label: "Legacy app", num: true, render: (r) => fmtInt(r.legacy_app?.current ?? null) },
                    { key: "ai", label: "Barsys AI app", num: true, render: (r) => (r.ai_app ? fmtInt(r.ai_app.current) : <span className="text-inverse text-opacity-50" title="Not tracked by the AI app backend">n/a</span>) },
                    { key: "aichg", label: "AI vs prev", num: true, render: (r) => <Change value={r.ai_app?.change_pct ?? null} /> },
                    { key: "share", label: "AI share", num: true, render: (r) => fmtPct(r.ai_app ? r.total.ai_app_share_pct : null) },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-5">
              <Section title="POURS BY MACHINE" subtitle="last 30 days">
                <Chart type="bar" height={220} series={machineChart.series} options={machineChart.options} />
                <DataTable
                  rows={machines}
                  columns={[
                    { key: "machine", label: "Machine" },
                    { key: "pours", label: "Pours", num: true },
                    { key: "users", label: "Users", num: true },
                    { key: "machines", label: "Machines", num: true },
                    { key: "ai_app_share_pct", label: "Via AI app", num: true, fmt: fmtPct },
                  ]}
                />
              </Section>
            </div>
          </div>

          <div className="row">
            <Tile label="Website sessions (30d)" value={fmtInt(w?.sessions)} sub={<Change value={w?.change_pct?.sessions} />} />
            <Tile label="Website visitors (30d)" value={fmtInt(w?.users)} sub={`${fmtPct(w?.engaged_pct)} engaged`} />
            <Tile label="Add-to-cart sessions" value={fmtInt(w?.add_to_cart_sessions)} />
            <Tile label="Web purchases (GA)" value={fmtInt(w?.purchases)} sub={<Change value={w?.change_pct?.purchases} />} />
            <Tile label="Web revenue (GA)" value={fmtMoney(w?.revenue)} sub={`${fmtPct(w?.conversion_pct)} conversion`} />
            <Tile label="Pipeline alerts (24h)" value={fmtInt(data.freshness?.pipeline_alerts_24h)} sub={data.freshness?.events_ingested_at ? `events through ${String(data.freshness.events_ingested_at).slice(0, 16).replace("T", " ")} UTC` : null} />
          </div>

          <div className="row">
            <div className="col-xl-7">
              <Section title="BRAZE CAMPAIGNS" subtitle={b?.period ? `${b.period.from} → ${b.period.to}` : "no data"} bodyClass="p-0">
                <div className="d-flex flex-wrap gap-4 px-3 pt-3 small">
                  <div>Sent <b>{fmtInt(b?.totals?.sent)}</b></div>
                  <div>Delivered <b>{fmtPct(b?.rates?.delivery_pct)}</b></div>
                  <div>Opened <b>{fmtPct(b?.rates?.open_pct)}</b></div>
                  <div>Clicked <b>{fmtPct(b?.rates?.click_pct)}</b></div>
                  <div>Conversions <b>{fmtInt(b?.totals?.conversions)}</b></div>
                </div>
                <DataTable
                  rows={b?.top_campaigns}
                  columns={[
                    { key: "campaign_name", label: "Campaign", width: 360 },
                    { key: "sent", label: "Sent", num: true },
                    { key: "delivered", label: "Delivered", num: true },
                    { key: "opens", label: "Opens", num: true },
                    { key: "clicks", label: "Clicks", num: true },
                    { key: "conversions", label: "Conv.", num: true },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-5">
              <Section title="BRAZE CANVASES" subtitle="journeys, same period" bodyClass="p-0">
                <DataTable
                  rows={b?.top_canvases}
                  columns={[
                    { key: "canvas_name", label: "Canvas", width: 300 },
                    { key: "entries", label: "Entries", num: true },
                    { key: "conversions", label: "Conv.", num: true },
                  ]}
                />
              </Section>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-4">
              <Section title="TOP SEARCHES" subtitle={`legacy app, last ${data.window_days} days, terms searched by 3+ users`} bodyClass="p-0">
                <DataTable
                  maxHeight={420}
                  rows={data.top_searches}
                  empty="No searches in this period"
                  columns={[
                    { key: "term", label: "Search term", width: 220 },
                    { key: "searches", label: "Searches", num: true },
                    { key: "users", label: "Users", num: true },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-4">
              <Section title="TOP DRINKS" subtitle={`most crafted, last ${data.window_days} days`} bodyClass="p-0">
                <DataTable
                  maxHeight={420}
                  rows={data.top_drinks}
                  empty="No drinks crafted in this period"
                  columns={[
                    { key: "recipe_name", label: "Drink", width: 220 },
                    { key: "crafts", label: "Crafts", num: true },
                    { key: "users", label: "Users", num: true },
                    { key: "ai_app_share_pct", label: "Via AI app", num: true, fmt: fmtPct },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-4">
              <Section title="TOP INGREDIENTS" subtitle={`most poured, last ${data.window_days} days`} bodyClass="p-0">
                <DataTable
                  maxHeight={420}
                  rows={data.top_ingredients}
                  empty="No pours in this period"
                  columns={[
                    { key: "ingredient", label: "Ingredient", width: 180 },
                    { key: "pours", label: "Pours", num: true },
                    { key: "users", label: "Users", num: true },
                    { key: "type", label: "Type", render: (r) => r.type || "—" },
                    { key: "zero_proof", label: "0% ABV", render: (r) => (r.zero_proof === true ? "Yes" : r.zero_proof === false ? "No" : "—") },
                    { key: "sponsor", label: "Sponsor", render: (r) => r.sponsor || "—" },
                  ]}
                />
              </Section>
            </div>
          </div>

          <div className="small text-inverse text-opacity-50 mb-4">
            Source: BigQuery. App activity from the unified event pipeline (legacy app = MySQL, Barsys AI app = Postgres; ~15 min lag). Website from the GA4 export (GA-tracked purchases; order records stay on E-Commerce). Braze from the daily Braze API export. Searches are logged by the legacy app only; ingredient type / 0% ABV / sponsor come from the ingredient catalogue.
          </div>
        </div>
      )}
    </div>
  );
}
